import { h, createContext } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import * as queries from 'utils/storefront/queries';
import {
  formatDiscountConnectors,
  formatCartLinesAjax,
} from 'utils/storefront/format-resource';
import CartService, { CART_EVENTS } from './cart-service';
import Storefront from '../utils/storefront';
import { getCookie } from '../utils/helpers';

const Context = createContext();

const CartContext = ({ children, data }) => {
  const storefront = new Storefront();
  const [cartState, setCartState] = useState(CartService.cart);
  const [loading, setLoading] = useState(CartService.loading);
  const [discountConnectors, setDiscountConnectors] = useState(null);

  const getDiscountConnectors = async () => {
    try {
      const response = await storefront.request({
        query: queries.discountConnectors,
        variables: {
          countryCode: window.TMW.localization.countryCode,
          languageCode: window.TMW.localization.languageCode,
        },
      });

      const formattedDiscountConnectors = formatDiscountConnectors(response);
      setDiscountConnectors(formattedDiscountConnectors);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  useEffect(() => {
    const listener = (event) => {
      setLoading(CartService.loading);
      setCartState(CartService.cart);
      if (event === CART_EVENTS.UPDATE) {
        document.dispatchEvent(new CustomEvent('cart:open'));
      }
    };
    CartService.addListener(listener);
    setCartState(CartService.cart);
    getDiscountConnectors();
    return () => {
      CartService.removeListener(listener);
    };
  }, []);

  /**
   * Wrapper for cart API to make interactions more simple
   */
  const cart = {
    add: (...params) => CartService.add(...params),
    remove: (...params) => CartService.remove(...params),
    update: (...params) => CartService.update(...params),
    state: cartState,
  };

  const handleExternalAddToCart = (e) => cart.add(e.detail.payload);

  const getAttributes = (line) => {
    const attributes =
      line.attributes ||
      Object.entries(line.properties || {}).map(([key, value]) => ({
        key,
        value,
      }));
    return attributes;
  };

  const getMerchandiseId = (line) =>
    line.merchandise?.product?.id || `gid://shopify/Product/${line.product_id}`;

  useEffect(() => {
    if (!cartState.id && !cartState.token) return;
    const lines =
      cart?.mode === 'storefront'
        ? cart?.state?.lines
        : formatCartLinesAjax(cart?.state?.items);
    const totalAmount =
      cartState.cost?.totalAmount?.amount || cartState.items_subtotal_price;
    const cartCountEl = document.querySelector('[data-cart-count]');
    const addPayload = [];
    const updatePayload = [];
    cartCountEl.textContent =
      lines.reduce((total, line) => total + line.quantity, 0) || 0;

    // Setup cart events for components outside of this context
    document.addEventListener('cart:add', handleExternalAddToCart);

    if (discountConnectors) {
      discountConnectors.map((connector) => {
        if (connector.discount_type === 'product') {
          const gwpRemovedByUser = getCookie(
            `disabled_gwp-${connector.handle}`,
          );

          const isMultipleAdd = !!connector.gwp_product.tags.find(
            (tag) => tag === 'gwp-allow-multiple',
          );

          const qualifyingProductInCart = lines.find(
            (line) => getMerchandiseId(line) === connector.qualify_product?.id,
          );

          const qtyToAdd =
            isMultipleAdd && !!qualifyingProductInCart?.quantity
              ? qualifyingProductInCart.quantity
              : 1;

          const gwpInCart = lines.find((line) =>
            getAttributes(line).find(
              (attribute) =>
                attribute.key === '_gwp_handle' &&
                attribute.value === connector.handle,
            ),
          );
          // add gwp to cart
          // if qualifying product is in cart and
          // gwp is not in cart
          if (!gwpRemovedByUser && qualifyingProductInCart && !gwpInCart) {
            const gwpAddPayload = {
              attributes: [
                {
                  key: '_gwp',
                  value: 'true',
                },
                {
                  key: '_gwp_handle',
                  value: connector.handle,
                },
                {
                  key: '_gwp_message',
                  value: connector.gift_message,
                },
                {
                  key: '_gwp_product',
                  value: connector.qualify_product.id.replace(
                    'gid://shopify/Product/',
                    '',
                  ),
                },
                {
                  key: '_gwp_price',
                  value:
                    connector.gwp_product?.variants?.edges[0]?.node?.price
                      ?.amount,
                },
              ],
              id: connector.gwp_product.variants.edges[0].node.id,
              quantity: qtyToAdd,
            };
            addPayload.push(gwpAddPayload);
          }

          // limit quantity of GWP to 1
          // if qualifying product is in cart and
          // gwp is in cart but quantity is > 1
          if (
            qualifyingProductInCart &&
            gwpInCart &&
            gwpInCart?.quantity !== qtyToAdd
          ) {
            const gwpQtyControlPayload = {
              id: lines.find((line) =>
                getAttributes(line).find(
                  (attribute) =>
                    attribute.key === '_gwp_handle' &&
                    attribute.value === connector.handle,
                ),
              ).key,
              quantity: qtyToAdd,
            };
            updatePayload.push(gwpQtyControlPayload);
          }

          if (!qualifyingProductInCart && gwpInCart) {
            updatePayload.push({
              id: gwpInCart.key,
              quantity: 0,
            });
          }
        } else if (connector.discount_type === 'cartPrice') {
          const gwpRemovedByUser = getCookie(
            `disabled_gwp-${connector.handle}`,
          );

          const threshold =
            window.Shopify.currency.active === 'CAD'
              ? connector.gwp_threshold_ca
              : connector.gwp_threshold;

          const totalAmountMeetsThreshold =
            totalAmount > parseFloat(threshold * 100);

          const gwpInCart = lines.find((line) =>
            getAttributes(line).find(
              (attribute) =>
                attribute.key === '_gwp_handle' &&
                attribute.value === connector.handle,
            ),
          );

          // add gwp to cart
          // if cart total is > threshold and
          // gwp is not in cart
          if (!gwpRemovedByUser && totalAmountMeetsThreshold && !gwpInCart) {
            const gwpAddPayload = {
              attributes: [
                {
                  key: '_gwp',
                  value: 'true',
                },
                {
                  key: '_gwp_handle',
                  value: connector.handle,
                },
                {
                  key: '_gwp_message',
                  value: connector.gift_message,
                },
                {
                  key: '_gwp_threshold',
                  value: connector.gwp_threshold,
                },
                {
                  key: '_gwp_threshold_ca',
                  value: connector.gwp_threshold_ca,
                },
                {
                  key: '_gwp_price',
                  value:
                    connector.gwp_product?.variants?.edges[0]?.node?.price
                      ?.amount,
                },
              ],
              id: connector.gwp_product.variants.edges[0].node.id,
              quantity: 1,
            };
            addPayload.push(gwpAddPayload);
          }

          // no need to remove the GWP if invalid
          // that is handled by the line item script

          // update quantity of GWP
          // if cart total > threshold and
          // GWP is in cart but has quantity > 1
          if (totalAmountMeetsThreshold && gwpInCart?.quantity > 1) {
            const gwpQtyControlPayload = [
              {
                id: lines.find((line) =>
                  getAttributes(line).find(
                    (attribute) =>
                      attribute.key === '_gwp_handle' &&
                      attribute.value === connector.handle,
                  ),
                ).key,
                quantity: 1,
              },
            ];
            updatePayload.push(gwpQtyControlPayload);
          }

          if (!totalAmountMeetsThreshold && gwpInCart) {
            updatePayload.push({
              id: gwpInCart.key,
              quantity: 0,
            });
          }
        }

        return '';
      });

      // SAMPLES
      //
      // Only one sample should be in the cart at a time
      // there can be more than one sample configured, each with a different price threshold
      // the sample with the highest threshold that is still below the cart price
      //   is the sample that should be added to the cart.
      // all other samples should be removed from the cart.

      const sampleDiscounts = discountConnectors
        .filter((discount) => discount.discount_type === 'sample')
        .sort(
          (a, b) => parseFloat(b.gwp_threshold) - parseFloat(a.gwp_threshold),
        );

      // mostFitSample is the sample with the highest price below the cart price
      // and that has not been removed by the user (cookie)

      const mostFitSample = sampleDiscounts.find((connector) => {
        const threshold =
          window.Shopify.currency.active === 'CAD'
            ? connector.gwp_threshold_ca
            : connector.gwp_threshold;

        return (
          totalAmount > parseFloat(threshold * 100) &&
          !getCookie(`disabled_gwp-${connector.handle}`)
        );
      });

      const mostFitSampleInCart =
        mostFitSample &&
        lines.find((line) =>
          getAttributes(line).find(
            (attribute) =>
              attribute.key === '_isSample' &&
              attribute.value === mostFitSample.handle,
          ),
        );

      // any samples in the cart that dont match the mostFitSample
      const unfitSamplesInCart = mostFitSample
        ? lines.filter((line) =>
            getAttributes(line).find(
              (attribute) =>
                attribute.key === '_isSample' &&
                attribute.value !== mostFitSample.handle,
            ),
          )
        : lines.filter((line) =>
            getAttributes(line).find(
              (attribute) => attribute.key === '_isSample',
            ),
          );

      // remove all unfit samples
      // the line item script only removes an item if it doesnt qualify based on price
      // it doesnt make sure only the correct sample is in the cart
      // so we need to do that here.
      if (unfitSamplesInCart && unfitSamplesInCart.length > 0) {
        // eslint-disable-next-line array-callback-return
        unfitSamplesInCart.map((sample) => {
          updatePayload.push({
            id: sample.key,
            quantity: 0,
          });
        });
      }

      // add most fit sample to cart
      // if valid sample meets threshold and
      // sample is not in cart
      if (mostFitSample && !mostFitSampleInCart) {
        const mostFitSampleAddPayload = {
          attributes: [
            {
              key: '_gwp',
              value: 'true',
            },
            {
              key: '_gwp_handle',
              value: mostFitSample.handle,
            },
            {
              key: '_gwp_message',
              value: mostFitSample.gift_message,
            },
            {
              key: '_gwp_threshold',
              value: mostFitSample.gwp_threshold,
            },
            {
              key: '_gwp_threshold_ca',
              value: mostFitSample.gwp_threshold_ca,
            },
            {
              key: '_isSample',
              value: mostFitSample.handle,
            },
          ],
          id: mostFitSample.gwp_product.variants.edges[0].node.id,
          quantity: 1,
        };
        addPayload.push(mostFitSampleAddPayload);
      }

      // restrict the quantity to 1
      // if valid sample meets threshold and
      // that sample is in the cart but with quantity > 1
      if (mostFitSample && mostFitSampleInCart?.quantity > 1) {
        const sampleQtyControlPayload = [
          {
            id: lines.find((line) =>
              getAttributes(line).find(
                (attribute) =>
                  attribute.key === '_isSample' &&
                  attribute.value === mostFitSample?.handle,
              ),
            ).key,
            quantity: 1,
          },
        ];
        updatePayload.push(sampleQtyControlPayload);
      }

      if (!mostFitSample && mostFitSampleInCart) {
        updatePayload.push({
          id: mostFitSampleInCart.key,
          quantity: 0,
        });
      }
    }

    if (cartState.item_count > 0 && cartState.total_price === 0) {
      // eslint-disable-next-line array-callback-return, consistent-return
      cartState.items.map((item) => {
        if (!updatePayload.find((updateItem) => updateItem.key === item.key)) {
          updatePayload.push({
            id: item.key,
            quantity: 0,
          });
        }
      });
    }

    if (updatePayload.length > 0) {
      cart.update(updatePayload, true);
    }

    if (addPayload.length > 0) {
      cart.add(addPayload);
    }

    // Cleanup
    // eslint-disable-next-line consistent-return
    return () =>
      document.removeEventListener('cart:add', handleExternalAddToCart);
  }, [cartState.item_count, discountConnectors]);

  return (
    <Context.Provider value={{ cart, cartLoading: loading, data }}>
      {children}
    </Context.Provider>
  );
};

export const withCartContext = (Rendered) => (props) =>
  (
    <Context.Consumer>
      {(value) => <Rendered {...props} {...value} />}
    </Context.Consumer>
  );

export default CartContext;
